import { useState, useEffect, useCallback } from 'react';
import { rrhhApi } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { TableSkeleton } from '../../lib/skeletons';
import { toast } from '../../hooks/use-toast';
import { Users, AlertTriangle, Clock, UtensilsCrossed, CalendarDays, ChevronLeft, ChevronRight, Briefcase } from 'lucide-react';

const ESTADO_STYLES = {
  P: 'bg-emerald-500/15 text-emerald-700', F: 'bg-red-500/15 text-red-700',
  L: 'bg-purple-500/15 text-purple-700', V: 'bg-cyan-500/15 text-cyan-700',
  O: 'bg-orange-500/15 text-orange-700',
};
const ESTADO_LABELS = { P: 'Presente', F: 'Falla', L: 'Licencia', V: 'Vacaciones', R: 'Retiro', O: 'Otros' };
const TIPO_COLORS = { A: 'text-emerald-600', H: 'text-amber-600', C: 'text-blue-600' };

function formatDate(d) { return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`; }
function shiftDate(fecha, days) {
  const [dd, mm, yyyy] = fecha.split('/');
  const d = new Date(parseInt(yyyy), parseInt(mm) - 1, parseInt(dd));
  d.setDate(d.getDate() + days);
  return formatDate(d);
}

export default function PanelPersonalPage() {
  const [fecha, setFecha] = useState(formatDate(new Date()));
  const [vista, setVista] = useState('diario');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await rrhhApi.getPanelPersonal({ fecha, vista });
      setData(res.data);
    } catch {
      toast({ title: 'Error al cargar panel', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [fecha, vista]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading && !data) return <TableSkeleton rows={10} cols={8} />;

  return (
    <div className="space-y-4 page-enter" data-testid="panel-personal">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Panel de Personal</h1>
          <p className="text-sm text-muted-foreground">Distribución, asistencia y atrasos</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-xl border overflow-hidden">
            {['diario', 'semanal', 'mensual'].map(v => (
              <button key={v} onClick={() => setVista(v)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${vista === v ? 'bg-primary text-white' : 'bg-background hover:bg-muted'}`}
                data-testid={`btn-vista-${v}`}>
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-8 w-8 rounded-xl" onClick={() => setFecha(shiftDate(fecha, vista === 'semanal' ? -7 : vista === 'mensual' ? -30 : -1))}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium px-2 min-w-[90px] text-center">{fecha}</span>
            <Button variant="outline" size="icon" className="h-8 w-8 rounded-xl" onClick={() => setFecha(shiftDate(fecha, vista === 'semanal' ? 7 : vista === 'mensual' ? 30 : 1))}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {vista === 'diario' && data && <VistaDiaria d={data} onReload={loadData} />}
      {vista === 'semanal' && data && <VistaSemanal d={data} />}
      {vista === 'mensual' && data && <VistaMensual d={data} />}
    </div>
  );
}

function VistaDiaria({ d, onReload }) {
  const aus = d.ausencias || {};
  const loadData = onReload;
  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-3 md:grid-cols-7 gap-2">
        <KPI icon={<Users className="h-3.5 w-3.5 text-primary" />} label="Activos" value={d.total_activos} bg="bg-primary/10" />
        <KPI icon={<Users className="h-3.5 w-3.5 text-emerald-600" />} label="Presentes" value={d.total_presentes} bg="bg-emerald-500/10" />
        <KPI icon={<AlertTriangle className="h-3.5 w-3.5 text-red-500" />} label="Falla" value={aus.F || 0} bg="bg-red-500/10" />
        <KPI icon={<Clock className="h-3.5 w-3.5 text-blue-600" />} label="Permiso" value={aus.P || 0} bg="bg-blue-500/10" />
        <KPI icon={<CalendarDays className="h-3.5 w-3.5 text-purple-600" />} label="Licencia" value={aus.L || 0} bg="bg-purple-500/10" />
        <KPI icon={<CalendarDays className="h-3.5 w-3.5 text-cyan-600" />} label="Vacaciones" value={aus.V || 0} bg="bg-cyan-500/10" />
        <KPI icon={<UtensilsCrossed className="h-3.5 w-3.5 text-amber-600" />} label="Almuerzos" value={d.total_almuerzos} bg="bg-amber-500/10" />
      </div>

      {/* Distribución por OT */}
      {d.distribucion_ot?.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-xs" data-testid="tabla-distribucion-ot">
              <thead><tr className="bg-muted/50 border-b">
                <th className="px-3 py-2 text-left font-medium">OT</th>
                <th className="px-3 py-2 text-left font-medium">Descripción</th>
                <th className="px-3 py-2 text-left font-medium">Lugar</th>
                <th className="px-3 py-2 text-center font-medium">Presentes</th>
                <th className="px-3 py-2 text-center font-medium">Almuerzos</th>
                <th className="px-3 py-2 text-center font-medium">A</th>
                <th className="px-3 py-2 text-center font-medium">H</th>
                <th className="px-3 py-2 text-center font-medium">C</th>
              </tr></thead>
              <tbody>
                {d.distribucion_ot.map((ot) => (
                  <tr key={ot.ot} className="border-b hover:bg-muted/20">
                    <td className="px-3 py-2 font-bold">{ot.ot}</td>
                    <td className="px-3 py-2 truncate max-w-[200px]">{ot.descripcion}</td>
                    <td className="px-3 py-2 text-muted-foreground">{ot.lugar}</td>
                    <td className="px-3 py-2 text-center font-bold">{ot.presentes}</td>
                    <td className="px-3 py-2 text-center font-bold text-amber-600">{ot.almuerzos}</td>
                    <td className="px-3 py-2 text-center text-emerald-600">{ot.tipo_counts?.A || ''}</td>
                    <td className="px-3 py-2 text-center text-amber-600">{ot.tipo_counts?.H || ''}</td>
                    <td className="px-3 py-2 text-center text-blue-600">{ot.tipo_counts?.C || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Atrasos del día */}
      {d.atrasos_hoy?.length > 0 && (
        <Card>
          <CardContent className="p-3">
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-2 text-red-600">
              <Clock className="h-4 w-4" /> Atrasos del día ({d.atrasos_hoy.length})
            </h3>
            <div className="flex flex-wrap gap-2">
              {d.atrasos_hoy.map((a, i) => (
                <span key={a.nombre || `at-${i}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-red-50 dark:bg-red-900/20 text-xs">
                  <span className="font-medium">{a.nombre}</span>
                  <span className="text-red-600 font-bold">{a.minutos}min</span>
                  <span className="text-muted-foreground text-[10px]">({a.hora_entrada})</span>
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Personal NO asignado — clasificar ausencia rápido */}
      {d.no_asignados?.length > 0 && (
        <NoAsignadosPanel noAsignados={d.no_asignados} fecha={d.fecha} canEdit={d.can_edit} onReload={loadData} />
      )}

      {/* Personal separado por cliente en columnas */}
      {d.personal_por_cliente && Object.keys(d.personal_por_cliente).length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="personal-grid">
          {Object.entries(d.personal_por_cliente).sort(([a],[b]) => a.localeCompare(b)).map(([cliente, workers]) => {
            const pres = workers.filter(w => w.estado === 'P').length;
            const aus = workers.filter(w => w.estado !== 'P').length;
            return (
              <Card key={cliente} className="overflow-hidden">
                <div className="px-3 py-2 bg-muted/40 border-b flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Briefcase className="h-3.5 w-3.5 text-primary" />
                    <span className="font-semibold text-sm">{cliente}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[10px]">
                    <span className="text-emerald-600 font-bold">{pres}P</span>
                    {aus > 0 && <span className="text-red-500 font-bold">{aus}A</span>}
                    <span className="text-muted-foreground">{workers.length} total</span>
                  </div>
                </div>
                <CardContent className="p-2 space-y-1">
                  {workers.map((p, i) => {
                    const isPresent = p.estado === 'P';
                    const hasAtraso = p.atraso_min > 0;
                    return (
                      <div key={p.numero || `w-${i}`} className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs border ${isPresent ? 'bg-emerald-50/50 dark:bg-emerald-900/10 border-emerald-200/50' : 'bg-red-50/50 dark:bg-red-900/10 border-red-200/50'} ${hasAtraso ? 'ring-1 ring-red-300' : ''}`}>
                        <span className={`w-5 h-5 leading-5 rounded text-center text-[9px] font-bold shrink-0 ${isPresent ? ESTADO_STYLES.P : ESTADO_STYLES[p.estado] || 'bg-muted text-muted-foreground'}`}>
                          {p.estado}
                        </span>
                        <span className="truncate flex-1 font-medium" title={p.nombre}>{p.nombre}</span>
                        {p.almuerzo && <span className={`text-[9px] font-bold ${TIPO_COLORS[p.tipo_almuerzo] || ''}`}>{p.tipo_almuerzo}</span>}
                        {hasAtraso && <span className="text-[9px] text-red-500 font-bold">{p.atraso_min}m</span>}
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function VistaSemanal({ d }) {
  return (
    <div className="space-y-4">
      <div className="text-sm text-muted-foreground">{d.semana_desde} al {d.semana_hasta}</div>

      {/* Resumen por día */}
      <Card>
        <CardContent className="p-0">
          <table className="w-full text-xs">
            <thead><tr className="bg-muted/50 border-b">
              <th className="px-3 py-2 text-left">Día</th>
              <th className="px-3 py-2 text-center">Presentes</th>
              <th className="px-3 py-2 text-center text-red-600">Falla</th>
              <th className="px-3 py-2 text-center text-blue-600">Permiso</th>
              <th className="px-3 py-2 text-center text-purple-600">Licencia</th>
              <th className="px-3 py-2 text-center text-cyan-600">Vacac.</th>
              <th className="px-3 py-2 text-center text-amber-600">Almuerzos</th>
            </tr></thead>
            <tbody>
              {(d.dias || []).map((dia) => (
                <tr key={dia.fecha} className="border-b hover:bg-muted/20">
                  <td className="px-3 py-2 font-medium">{dia.dia} <span className="text-muted-foreground">{dia.fecha}</span></td>
                  <td className="px-3 py-2 text-center font-bold">{dia.presentes}</td>
                  <td className="px-3 py-2 text-center text-red-600">{dia.ausencias?.F || ''}</td>
                  <td className="px-3 py-2 text-center text-blue-600">{dia.ausencias?.P || ''}</td>
                  <td className="px-3 py-2 text-center text-purple-600">{dia.ausencias?.L || ''}</td>
                  <td className="px-3 py-2 text-center text-cyan-600">{dia.ausencias?.V || ''}</td>
                  <td className="px-3 py-2 text-center text-amber-600 font-bold">{dia.almuerzos}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Atrasos de la semana */}
      {d.atrasos?.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <div className="px-3 py-2 bg-red-50 dark:bg-red-900/10 border-b">
              <h3 className="text-sm font-semibold text-red-700 flex items-center gap-2">
                <Clock className="h-4 w-4" /> Atrasos de la semana ({d.atrasos.length} trabajadores)
              </h3>
            </div>
            <table className="w-full text-xs">
              <thead><tr className="bg-muted/50 border-b">
                <th className="px-3 py-2 text-left">Trabajador</th>
                {(d.week_dates || []).map((wd, i) => (
                  <th key={i} className="px-2 py-2 text-center">{['Lu', 'Ma', 'Mi', 'Ju', 'Vi'][i]}</th>
                ))}
                <th className="px-2 py-2 text-center font-bold border-l">Semana</th>
                <th className="px-2 py-2 text-center font-bold">Mes (#)</th>
                <th className="px-2 py-2 text-center font-bold">Mes (min)</th>
              </tr></thead>
              <tbody>
                {d.atrasos.map((a, i) => (
                  <tr key={a.nombre || `atr-${i}`} className="border-b hover:bg-muted/20">
                    <td className="px-3 py-1.5 font-medium">{a.nombre}</td>
                    {(a.dias || []).map((m, j) => (
                      <td key={j} className={`px-2 py-1.5 text-center tabular-nums ${m > 0 ? 'text-red-600 font-bold' : 'text-muted-foreground'}`}>
                        {m > 0 ? `${m}m` : '-'}
                      </td>
                    ))}
                    <td className="px-2 py-1.5 text-center font-bold border-l tabular-nums text-red-700">{a.total_semana}m</td>
                    <td className="px-2 py-1.5 text-center tabular-nums">{a.atrasos_mes_count}</td>
                    <td className="px-2 py-1.5 text-center tabular-nums text-red-600">{a.atrasos_mes_min}m</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function VistaMensual({ d }) {
  return (
    <div className="space-y-4">
      <div className="text-sm font-medium">{d.mes_nombre} — {d.total_trabajadores_con_datos} trabajadores con datos</div>

      {/* Daily chart-like grid */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-[10px]">
            <thead><tr className="bg-muted/50 border-b">
              <th className="px-1 py-1.5 text-center font-medium">Día</th>
              {(d.resumen_dias || []).map(rd => (
                <th key={rd.dia} className="px-0.5 py-1.5 text-center font-medium w-8 min-w-[32px]">{rd.dia}</th>
              ))}
            </tr></thead>
            <tbody>
              <tr className="border-b">
                <td className="px-1 py-1 font-medium text-emerald-700">Pres</td>
                {(d.resumen_dias || []).map(rd => (
                  <td key={rd.dia} className={`px-0.5 py-1 text-center tabular-nums ${rd.presentes > 0 ? 'font-bold' : 'text-muted-foreground'}`}>{rd.presentes || ''}</td>
                ))}
              </tr>
              <tr className="border-b">
                <td className="px-1 py-1 font-medium text-red-600">Falla</td>
                {(d.resumen_dias || []).map(rd => (
                  <td key={rd.dia} className="px-0.5 py-1 text-center tabular-nums text-red-500">{rd.ausencias?.F || ''}</td>
                ))}
              </tr>
              <tr className="border-b">
                <td className="px-1 py-1 font-medium text-amber-600">Alm</td>
                {(d.resumen_dias || []).map(rd => (
                  <td key={rd.dia} className="px-0.5 py-1 text-center tabular-nums text-amber-600">{rd.almuerzos || ''}</td>
                ))}
              </tr>
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Top atrasos del mes */}
      {d.top_atrasos?.length > 0 && d.top_atrasos[0]?.atrasos_count > 0 && (
        <Card>
          <CardContent className="p-3">
            <h3 className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-2">
              <Clock className="h-4 w-4" /> Top Atrasos del Mes
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {d.top_atrasos.filter(a => a.atrasos_count > 0).map((a, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg bg-red-50/50 dark:bg-red-900/10 border border-red-100 text-xs">
                  <span className="font-medium truncate">{a.nombre}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-red-600 font-bold">{a.atrasos_count}x</span>
                    <span className="text-muted-foreground">{a.atrasos_min}min</span>
                    <span className="text-xs">P:{a.dias_presente} F:{a.F} L:{a.L} V:{a.V}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function KPI({ icon, label, value, bg }) {
  return (
    <Card><CardContent className="p-2.5 flex items-center gap-2">
      <div className={`p-1 rounded-lg ${bg}`}>{icon}</div>
      <div><p className="text-[9px] text-muted-foreground">{label}</p><p className="text-sm font-bold tabular-nums">{value}</p></div>
    </CardContent></Card>
  );
}

const AUSENCIA_TYPES = [
  { v: 'F', label: 'Falla', color: 'bg-red-500/15 text-red-700' },
  { v: 'V', label: 'Vacaciones', color: 'bg-cyan-500/15 text-cyan-700' },
  { v: 'P', label: 'Permiso', color: 'bg-blue-500/15 text-blue-700' },
  { v: 'L', label: 'Licencia', color: 'bg-purple-500/15 text-purple-700' },
  { v: 'R', label: 'Retiro (Finiquitado)', color: 'bg-gray-500/15 text-gray-700' },
  { v: 'O', label: 'Otros', color: 'bg-orange-500/15 text-orange-700' },
];

function NoAsignadosPanel({ noAsignados, fecha, canEdit, onReload }) {
  const [filtro, setFiltro] = useState('');
  const list = noAsignados.filter(w => (w.nombre || '').toLowerCase().includes(filtro.toLowerCase()));

  const clasificar = async (workerNum, tipo) => {
    try {
      await rrhhApi.clasificarAusencia({
        fecha, trabajador_numero: workerNum, ausencia: tipo,
      });
      toast({ title: `Registrado como ${AUSENCIA_TYPES.find(x => x.v === tipo)?.label || tipo}` });
      onReload();
    } catch (err) {
      toast({
        title: err.response?.data?.detail || 'Error al clasificar',
        variant: 'destructive',
      });
    }
  };

  return (
    <Card data-testid="panel-no-asignados">
      <CardContent className="p-3">
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            Personal sin asignar ({noAsignados.length})
          </h3>
          <p className="text-[11px] text-muted-foreground">
            {canEdit ? 'Clasifique su ausencia o agréguelo en un Parte Diario.' : 'Solo lectura. Requiere rol RRHH o admin para clasificar.'}
          </p>
          <div className="flex-1" />
          <input type="text" placeholder="Filtrar…" value={filtro}
            onChange={e => setFiltro(e.target.value)}
            className="h-7 w-40 rounded border bg-background px-2 text-xs"
            data-testid="input-filtro-no-asignados" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1.5 max-h-72 overflow-y-auto pr-1">
          {list.map(w => (
            <div key={w.numero} className="flex items-center gap-1.5 bg-muted/30 hover:bg-muted/50 rounded-md px-2 py-1.5 text-xs"
              data-testid={`no-asignado-${w.numero}`}>
              <span className="font-medium flex-1 truncate" title={w.nombre}>{w.nombre}</span>
              {canEdit && (
                <div className="flex items-center gap-0.5">
                  {AUSENCIA_TYPES.map(t => (
                    <button
                      key={t.v}
                      onClick={() => clasificar(w.numero, t.v)}
                      className={`w-5 h-5 rounded text-[10px] font-bold hover:ring-2 hover:ring-primary/40 transition ${t.color}`}
                      title={t.label}
                      data-testid={`btn-aus-${w.numero}-${t.v}`}
                    >{t.v}</button>
                  ))}
                </div>
              )}
            </div>
          ))}
          {list.length === 0 && (
            <p className="text-xs text-muted-foreground col-span-full text-center py-4">Sin resultados</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
